/* eslint-disable no-mixed-spaces-and-tabs */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import BASE_QUERY from '@/features/baseQuery';

// Creating an API service using RTK Query
export const notificationsApi = createApi({
	reducerPath: 'notificationsApi',
	tagTypes: ['NOTIFICATIONS_USER', 'NOTIFICATIONS_USERS', 'RELEASE_NOTIFICATION', 'EVENT_NOTIFICATIONS'],
	baseQuery: fetchBaseQuery({ ...BASE_QUERY, baseUrl: process.env.REACT_APP_NOTIFICATIONS_API }),
	keepUnusedDataFor: 900, // Cache will only clear after 900 seconds (15 minutes)
	endpoints: (builder) => ({
		// Defining an endpoint to get properties for specific pipeline
		getNotificationsUser: builder.query({
			providesTags: (result, error, args) => ['NOTIFICATIONS_USER'],
			query: ({ docId, userId, email, brokerGroupIds, type }) => ({
				url: '/users/get',
				method: 'POST',
				body: { docId, userId, email, brokerGroupIds, type },
			}),
			transformResponse: (response) => {
				console.log(
					'👾 ~ transform getNotificationsUser response',
					response,
					response?.data,
				);
				return response?.data;
			},
		}),
		// Defining an endpoint to get properties for specific pipeline
		listNotificationsUsers: builder.query({
			providesTags: (result, error, type) => [{ type: 'NOTIFICATIONS_USERS', id: type }],
			query: (type) => ({
				url: '/users/list',
				method: 'POST',
				body: { type },
			}),
			transformResponse: (response) => {
				console.log('👾 ~ transform listNotificationsUsers response', response);
				return response;
			},
		}),
		createNotificationUser: builder.mutation({
			invalidatesTags: (result, error, { type }) => [
				{ type: 'NOTIFICATIONS_USERS', id: type },
				'NOTIFICATIONS_USER',
			],
			query: ({ userId, email, type, actions }) => ({
				url: '/users/create',
				method: 'POST',
				body: { userId, email, type, actions },
			}),
			transformResponse: (response) => {
				console.log('👾 ~ transform createNotificationUser response', response);
				return response;
			},
		}),
		createNotification: builder.mutation({
			query: ({ dealId, taskId, clientId, action }) => ({
				url: '/create',
				method: 'POST',
				body: { dealId, taskId, clientId, action },
			}),
			transformResponse: (response) => {
				console.log('👾 ~ transform createNotification response', response);
				return response;
			},
		}),
		updateNotificationUser: builder.mutation({
			invalidatesTags: (result, error, { type }) => [
				{ type: 'NOTIFICATIONS_USERS', id: type },
				'NOTIFICATIONS_USER',
			],
			query: ({ docId, actions, type }) => ({
				url: '/users/update',
				method: 'POST',
				body: { docId, actions },
			}),
			transformResponse: (response) => {
				console.log('👾 ~ transform updateNotificationUser response', response);
				return response;
			},
		}),
		createOrUpdateNotificationUser: builder.mutation({
			queryFn: async (
				{ docId, email, userId, brokerGroupIds, type, actions },
				{ getState, dispatch },
			) => {
				try {
					const res = await dispatch(
						notificationsApi.endpoints.getNotificationsUser.initiate({
							docId,
							userId,
							email,
							brokerGroupIds,
							type,
						}),
					).unwrap();
					console.log('Existing doc', res);
					if (res?.id) {
						await dispatch(
							notificationsApi.endpoints.updateNotificationUser.initiate({
								docId: res.id,
								actions,
								type,
							}),
						);
						return { data: res.id };
					} else {
						const newUser = await dispatch(
							notificationsApi.endpoints.createNotificationUser.initiate({
								userId,
								email,
								type,
								actions: actions
									?.map((a) => (typeof a === 'string' ? a : a?.action))
									.filter(Boolean),
							}),
						);
						return {
							data: newUser,
						};
					}
				} catch (error) {
					console.error('Error creating/updating notification user', error);
					return { error: { status: 500, message: error.message } };
				}
			},
		}),
		getUserNotification: builder.query({
			providesTags: ['RELEASE_NOTIFICATION'],
			query: ({ userId, notificationType }) => ({
				url: '/release/get',
				method: 'POST',
				body: { userId, notificationType },
			}),
		}),
		dismissUserNotification: builder.mutation({
			query: ({ notificationId, userId, notificationType }) => ({
				url: '/release/update',
				method: 'POST',
				body: { notificationId, userId, notificationType, dismissed: true },
			}),
			// Optimistic cache update - immediately update the UI without waiting for API
			async onQueryStarted({ userId, notificationType }, { dispatch, queryFulfilled }) {
				// Optimistically update the cached notification data
				const patchResult = dispatch(
					notificationsApi.util.updateQueryData(
						'getUserNotification',
						{ userId, notificationType },
						(draft) => {
							// Mark notification as dismissed in the cache
							draft.hasNotification = false;
						},
					),
				);

				try {
					await queryFulfilled;
				} catch {
					// If the API call fails, revert the optimistic update
					patchResult.undo();
				}
			},
		}),
		listEventNotifications: builder.query({
			providesTags: ['EVENT_NOTIFICATIONS'],
			query: ({ status, limit = 50 } = {}) => ({
				url: '/events/list',
				method: 'POST',
				body: { status, limit },
			}),
		}),
		updateEventNotification: builder.mutation({
			invalidatesTags: ['EVENT_NOTIFICATIONS'],
			query: ({ notificationId, status }) => ({
				url: '/events/update',
				method: 'POST',
				body: { notificationId, status },
			}),
			async onQueryStarted({ notificationId, status }, { dispatch, queryFulfilled }) {
				const patchResults = [];
				for (const filterStatus of ['unread', 'read', 'archived', undefined]) {
					patchResults.push(
						dispatch(
							notificationsApi.util.updateQueryData(
								'listEventNotifications',
								filterStatus ? { status: filterStatus } : {},
								(draft) => {
									if (!draft?.notifications) return;
									const index = draft.notifications.findIndex(
										(n) => n.id === notificationId,
									);
									if (index === -1) return;
									if (filterStatus && filterStatus !== status) {
										draft.notifications.splice(index, 1);
										if (status !== 'unread' && draft.unreadCount > 0) {
											draft.unreadCount -= 1;
										}
									} else {
										draft.notifications[index].status = status;
									}
								},
							),
						),
					);
				}

				try {
					await queryFulfilled;
				} catch {
					patchResults.forEach((patch) => patch.undo());
				}
			},
		}),
		markAllEventNotificationsRead: builder.mutation({
			invalidatesTags: ['EVENT_NOTIFICATIONS'],
			query: () => ({
				url: '/events/mark-all-read',
				method: 'POST',
				body: {},
			}),
			async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
				const patchResult = dispatch(
					notificationsApi.util.updateQueryData(
						'listEventNotifications',
						{ status: 'unread' },
						(draft) => {
							if (draft) {
								draft.notifications = [];
								draft.unreadCount = 0;
							}
						},
					),
				);

				try {
					await queryFulfilled;
				} catch {
					patchResult.undo();
				}
			},
		}),
		archiveAllEventNotifications: builder.mutation({
			invalidatesTags: ['EVENT_NOTIFICATIONS'],
			query: () => ({
				url: '/events/archive-all',
				method: 'POST',
				body: {},
			}),
			async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
				const patchResults = ['unread', 'read'].map((status) =>
					dispatch(
						notificationsApi.util.updateQueryData(
							'listEventNotifications',
							{ status },
							(draft) => {
								if (draft) {
									draft.notifications = [];
									if (status === 'unread') draft.unreadCount = 0;
								}
							},
						),
					),
				);

				try {
					await queryFulfilled;
				} catch {
					patchResults.forEach((patch) => patch.undo());
				}
			},
		}),
	}),
});

// Exporting auto-generated hooks for the defined endpoints
export const {
	useGetNotificationsUserQuery,
	useCreateNotificationUserMutation,
	useListNotificationsUsersQuery,
	useUpdateNotificationUserMutation,
	useCreateOrUpdateNotificationUserMutation,
	useCreateNotificationMutation,
	useGetUserNotificationQuery,
	useDismissUserNotificationMutation,
	useListEventNotificationsQuery,
	useUpdateEventNotificationMutation,
	useMarkAllEventNotificationsReadMutation,
	useArchiveAllEventNotificationsMutation,
} = notificationsApi;
